import { NvForm, NvModal } from '@simwood/nv-react-components';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setVisibleExtensionMenu } from '../../../shared/reducers/client';
import { editPhoneExtension } from '../../../shared/reducers/contacts';
import { ApiPhoneExtensionWithSip } from '../../../shared/types/PhoneExtension';
import { StoreDispatch } from '../../store';

interface Props {
  extension: ApiPhoneExtensionWithSip;
}

interface FormValues {
  email: ApiPhoneExtensionWithSip['email'];
  autoAnswer: ApiPhoneExtensionWithSip['voicemailSettings']['autoAnswer'];
  pin: ApiPhoneExtensionWithSip['voicemailSettings']['pin'];
  playEnvelope: ApiPhoneExtensionWithSip['voicemailSettings']['playEnvelope'];
  playCallerId: ApiPhoneExtensionWithSip['voicemailSettings']['playCallerId'];
  allowReview: ApiPhoneExtensionWithSip['voicemailSettings']['allowReview'];
}

const extensionToFormValues = (
  extension: ApiPhoneExtensionWithSip,
): FormValues => ({
  email: extension.email,
  autoAnswer: extension.voicemailSettings.autoAnswer,
  pin: extension.voicemailSettings.pin,
  playEnvelope: extension.voicemailSettings.playEnvelope,
  playCallerId: extension.voicemailSettings.playCallerId,
  allowReview: extension.voicemailSettings.allowReview,
});

const formValuesToExtension = (
  extension: ApiPhoneExtensionWithSip,
  { email, ...rest }: FormValues,
): ApiPhoneExtensionWithSip => ({
  ...extension,
  email,
  voicemailSettings: {
    ...extension.voicemailSettings,
    ...rest,
  },
});

const VoicemailSettingsModal: React.FC<Props> = ({ extension }) => {
  const dispatch = useDispatch<StoreDispatch>();

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: extensionToFormValues(extension),
  });

  const onCancelClicked = () => {
    dispatch(setVisibleExtensionMenu(undefined));
  };

  const save = useCallback(
    (modifiedEntity: FormValues) => {
      if (isDirty) {
        dispatch(
          editPhoneExtension(formValuesToExtension(extension, modifiedEntity)),
        );
      }
    },
    [dispatch, isDirty, extension],
  );

  return (
    <NvModal.Form
      size='standard'
      title='Voicemail Settings'
      open={true}
      disabled={!isDirty}
      setOpen={(open) => {
        if (!open) {
          onCancelClicked();
        }
      }}
      closeOnSubmit
      submitText='Save'
      onSubmit={handleSubmit(save)}
    >
      <NvForm.Checkbox
        size='standard'
        labelText='Active'
        name='autoAnswer'
        control={control}
      />

      <NvForm.Input
        labelText='PIN'
        placeholder='PIN'
        inputProps={{ maxLength: 10, minLength: 4 }}
        name='pin'
        control={control}
        transform={{
          input: (x) => x,
          output: (x) => x?.replace(/\D/, '') || '',
        }}
      />

      <NvForm.Input
        labelText='Notification Email'
        type='email'
        name='email'
        control={control}
      />

      <NvForm.Checkbox
        size='standard'
        labelText='Include Time and Date'
        name='playEnvelope'
        control={control}
      />

      <NvForm.Checkbox
        size='standard'
        labelText='Include Caller Id'
        name='playCallerId'
        control={control}
      />

      <NvForm.Checkbox
        size='standard'
        labelText='Allow Review'
        name='allowReview'
        control={control}
      />
    </NvModal.Form>
  );
};

export default VoicemailSettingsModal;
